import type { CheckoutNetwork } from '../api/checkout';

/** Client-side format check aligned with backend `validateAddressForNetwork` expectations. */
export function isValidRefundAddress(address: string, network: CheckoutNetwork): boolean {
  const trimmed = address.trim();
  if (!trimmed) return false;

  switch (network) {
    case 'ETHEREUM':
    case 'BSC':
    case 'BASE':
    case 'ARBITRUM':
    case 'OPTIMISM':
    case 'POLYGON':
    case 'ROBINHOOD':
      return /^0x[a-fA-F0-9]{40}$/.test(trimmed);
    case 'SOLANA':
      return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed);
    case 'BITCOIN':
      return /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(trimmed);
    case 'TRON':
      return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(trimmed);
    case 'BITCOIN_CASH':
      return /^(bitcoincash:|bchtest:)?[qp][a-z0-9]{40,}$/i.test(trimmed) ||
        /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed);
    case 'LITECOIN':
      return /^(ltc1|tltc1|[LM3])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(trimmed);
    default:
      return false;
  }
}

/**
 * Address families keyed by the provider's chain slug, mirroring the server's
 * `BLOCKCHAIN_TO_FAMILY`. Unknown keys are absent on purpose: the server fails
 * closed on a chain it does not recognise, so a client that guessed would
 * promise a payment the server then refuses.
 */
const ORIGIN_ADDRESS_FAMILY: Record<string, RegExp> = (() => {
  const evm = /^0x[a-fA-F0-9]{40}$/;
  const solana = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  const bitcoin = /^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/;
  const litecoin = /^(ltc1|tltc1|[LM3])[a-zA-HJ-NP-Z0-9]{25,62}$/;
  const tron = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
  const ton = /^(-?\d+:[0-9a-fA-F]{64}|[A-Za-z0-9_+/-]{48})$/;
  const stellar = /^G[A-Z2-7]{55}$/;
  const near = /^([0-9a-f]{64}|(?=.{2,64}$)[a-z0-9]+([-_.][a-z0-9]+)*)$/;
  return {
    eth: evm,
    ethereum: evm,
    base: evm,
    arb: evm,
    arbitrum: evm,
    op: evm,
    optimism: evm,
    polygon: evm,
    pol: evm,
    matic: evm,
    hood: evm,
    robinhood: evm,
    bsc: evm,
    bnb: evm,
    avax: evm,
    avalanche: evm,
    gnosis: evm,
    linea: evm,
    scroll: evm,
    zksync: evm,
    blast: evm,
    mantle: evm,
    berachain: evm,
    sol: solana,
    solana: solana,
    btc: bitcoin,
    bitcoin: bitcoin,
    ltc: litecoin,
    litecoin: litecoin,
    tron: tron,
    trx: tron,
    ton: ton,
    stellar: stellar,
    xlm: stellar,
    near: near,
  };
})();

/**
 * Structural check for a refund address on a chain Cheddar does not custody.
 *
 * Shape only, and never the authority: the server re-validates with the
 * stricter `validateOriginAddress` (checksums, testnet detection, curve
 * checks) before anything irreversible happens. This exists to save the payer
 * a round trip, so it must not be *looser* than the server in a way that
 * promises success — hence the unknown-chain `false`.
 */
export function isValidOriginRefundAddress(
  blockchain: string,
  address: string,
): boolean {
  const trimmed = address.trim();
  if (!trimmed) return false;
  const pattern = ORIGIN_ADDRESS_FAMILY[blockchain.trim().toLowerCase()];
  if (!pattern) return false;
  return pattern.test(trimmed);
}
